import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	deleteAdminVocabCard,
	generateAdminVocabCardsAudioBulk,
	getAdminVocabCards,
	importAdminVocabCardsJson,
	type VocabCardAdminItem,
	type VocabCardImportResponse,
	type VocabCardLang,
	type VocabCardCefr,
} from '../../../../api/vocabcard';
import { listCategories } from '../../../../api/categories';

type CategoryOption = {
	id: number;
	title: string;
};

function getErrorMessage(e: any, fallback: string) {
	return (
		e?.response?.data?.error ||
		e?.response?.data?.message ||
		e?.data?.error ||
		e?.message ||
		fallback
	);
}

function ConfirmModal({
	open,
	title,
	description,
	loading,
	error,
	onCancel,
	onConfirm,
}: {
	open: boolean;
	title: string;
	description?: string;
	loading?: boolean;
	error?: string | null;
	onCancel: () => void;
	onConfirm: () => void;
}) {
	if (!open) return null;

	return (
		<div className="modal-overlay" onClick={onCancel}>
			<div className="modal" onClick={(e) => e.stopPropagation()}>
				<h3>{title}</h3>
				{description ? <p className="modal-desc">{description}</p> : null}
				{error ? <div className="modal-error">{error}</div> : null}
				<div className="modal-actions">
					<button onClick={onConfirm} disabled={loading}>
						{loading ? 'Usuwanie…' : 'Tak, usuń'}
					</button>
					<button className="secondary" onClick={onCancel} disabled={loading}>
						Anuluj
					</button>
				</div>
			</div>
		</div>
	);
}

const SOURCE_LANGS = ['', 'en', 'de', 'uk', 'it', 'fr', 'es', 'pl'];
const CEFR_LEVELS = ['', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function ImportJsonModal({
	open,
	loading,
	value,
	setValue,
	dryRun,
	setDryRun,
	updateExisting,
	setUpdateExisting,
	result,
	error,
	onClose,
	onSubmit,
	onFileLoad,
}: {
	open: boolean;
	loading: boolean;
	value: string;
	setValue: (v: string) => void;
	dryRun: boolean;
	setDryRun: (v: boolean) => void;
	updateExisting: boolean;
	setUpdateExisting: (v: boolean) => void;
	result: VocabCardImportResponse | null;
	error: string | null;
	onClose: () => void;
	onSubmit: () => void;
	onFileLoad: (file: File) => void;
}) {
	if (!open) return null;

	return (
		<div className="modal-overlay" onClick={onClose}>
			<div
				className="modal"
				onClick={(e) => e.stopPropagation()}
				style={{ maxWidth: 900, width: '95%' }}
			>
				<h3>Import słówek z JSON</h3>

				<p className="modal-desc">
					Wklej JSON albo załaduj plik <code>.json</code>. Obsługiwany jest format:
					<code>{` { dry_run, update_existing, defaults, items: [...] } `}</code>
					albo sama tablica <code>[...]</code>.
				</p>

				<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
					<label className="filter-checkbox">
						<input
							type="checkbox"
							checked={dryRun}
							onChange={(e) => setDryRun(e.target.checked)}
						/>
						Dry run
					</label>

					<label className="filter-checkbox">
						<input
							type="checkbox"
							checked={updateExisting}
							onChange={(e) => setUpdateExisting(e.target.checked)}
						/>
						Aktualizuj istniejące
					</label>

					<label className="btn ghost" style={{ cursor: 'pointer' }}>
						<span className="label">Wczytaj plik JSON</span>
						<input
							type="file"
							accept=".json,application/json"
							style={{ display: 'none' }}
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (file) onFileLoad(file);
								e.currentTarget.value = '';
							}}
						/>
					</label>
				</div>

				<textarea
					value={value}
					onChange={(e) => setValue(e.target.value)}
					placeholder="Wklej tutaj JSON..."
					style={{
						width: '100%',
						minHeight: 260,
						resize: 'vertical',
						padding: 12,
						borderRadius: 12,
						border: '1px solid rgba(255,255,255,0.1)',
						background: '#111',
						color: '#fff',
					}}
				/>

				{error ? (
					<div className="modal-error" style={{ marginTop: 12 }}>
						{error}
					</div>
				) : null}

				{result ? (
					<div style={{ marginTop: 16 }}>
						<h4 style={{ marginBottom: 8 }}>Raport importu</h4>
						<div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
							<span>
								Razem: <strong>{result.total}</strong>
							</span>
							<span>
								Created: <strong>{result.created}</strong>
							</span>
							<span>
								Updated: <strong>{result.updated}</strong>
							</span>
							<span>
								Skipped: <strong>{result.skipped}</strong>
							</span>
							<span>
								Errors: <strong>{result.errors}</strong>
							</span>
							<span>
								Dry run: <strong>{result.dry_run ? 'TAK' : 'NIE'}</strong>
							</span>
						</div>

						<div
							style={{
								maxHeight: 260,
								overflow: 'auto',
								border: '1px solid rgba(255,255,255,0.08)',
								borderRadius: 12,
							}}
						>
							<table className="contents-table">
								<thead>
									<tr>
										<th>#</th>
										<th>Term</th>
										<th>Język</th>
										<th>CEFR</th>
										<th>Status</th>
										<th>Błąd</th>
									</tr>
								</thead>
								<tbody>
									{result.results.map((row) => (
										<tr key={`${row.index}-${row.term}-${row.status}`}>
											<td>{row.index}</td>
											<td>{row.term || '—'}</td>
											<td>{row.source_lang || '—'}</td>
											<td>{row.cefr_level || '—'}</td>
											<td>{row.status}</td>
											<td>{row.error || '—'}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				) : null}

				<div className="modal-actions">
					<button className="secondary" onClick={onClose} disabled={loading}>
						Zamknij
					</button>
					<button onClick={onSubmit} disabled={loading || !value.trim()}>
						{loading ? 'Importowanie…' : 'Uruchom import'}
					</button>
				</div>
			</div>
		</div>
	);
}

export default function VocabCardList() {
	const navigate = useNavigate();

	const [items, setItems] = useState<VocabCardAdminItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [filtersVisible, setFiltersVisible] = useState(true);

	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [sourceLang, setSourceLang] = useState<VocabCardLang | ''>('');
	const [cefrLevel, setCefrLevel] = useState<VocabCardCefr | ''>('');
	const [categoryId, setCategoryId] = useState<string>('');
	const [isActive, setIsActive] = useState<string>('');

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);

	const [categories, setCategories] = useState<CategoryOption[]>([]);

	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [autoSelectCount, setAutoSelectCount] = useState<number>(20);
	const [bulkAudioLoading, setBulkAudioLoading] = useState(false);
	const [bulkAudioError, setBulkAudioError] = useState<string | null>(null);
	const [bulkAudioMessage, setBulkAudioMessage] = useState<string | null>(null);

	const [deleteId, setDeleteId] = useState<number | null>(null);
	const [deleteTerm, setDeleteTerm] = useState('');
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);

	const [searchParams, setSearchParams] = useSearchParams();

	const [importOpen, setImportOpen] = useState(false);
	const [importText, setImportText] = useState('');
	const [importDryRun, setImportDryRun] = useState(true);
	const [importUpdateExisting, setImportUpdateExisting] = useState(false);
	const [importLoading, setImportLoading] = useState(false);
	const [importError, setImportError] = useState<string | null>(null);
	const [importResult, setImportResult] = useState<VocabCardImportResponse | null>(null);

	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
		return () => clearTimeout(t);
	}, [search]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, sourceLang, cefrLevel, categoryId, isActive, pageSize]);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const data = await listCategories();
				const mapped = (Array.isArray(data) ? data : []).map((c: any) => ({
					id: c.id,
					title: c.title,
				}));
				setCategories(mapped);
			} catch {
				setCategories([]);
			}
		};

		fetchCategories();
	}, []);

	const fetchData = async () => {
		setLoading(true);
		try {
			const data = await getAdminVocabCards({
				page,
				page_size: pageSize,
				q: debouncedSearch || undefined,
				source_lang: sourceLang || undefined,
				cefr_level: cefrLevel || undefined,
				category_id: categoryId || undefined,
				is_active: isActive === '' ? undefined : isActive === 'true',
			});

			setItems(Array.isArray(data.items) ? data.items : []);
			setTotal(Number(data.pagination?.total || 0));
		} catch {
			setItems([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (searchParams.get('import') === '1') {
			setImportOpen(true);
		}
	}, [searchParams]);

	useEffect(() => {
		fetchData();
	}, [page, pageSize, debouncedSearch, sourceLang, cefrLevel, categoryId, isActive]);

	useEffect(() => {
		setSelectedIds((prev) => prev.filter((id) => items.some((item) => item.id === id)));
	}, [items]);

	const totalPages = Math.max(1, Math.ceil(total / pageSize));

	const itemsWithoutAudio = useMemo(
		() => items.filter((item) => !item.audio_path),
		[items]
	);

	const selectedItems = useMemo(
		() => items.filter((item) => selectedIds.includes(item.id)),
		[items, selectedIds]
	);

	const selectedWithoutAudioCount = useMemo(
		() => selectedItems.filter((item) => !item.audio_path).length,
		[selectedItems]
	);

	const allVisibleSelected = useMemo(() => {
		if (!items.length) return false;
		return items.every((item) => selectedIds.includes(item.id));
	}, [items, selectedIds]);

	const allMissingAudioSelected = useMemo(() => {
		if (!itemsWithoutAudio.length) return false;
		return itemsWithoutAudio.every((item) => selectedIds.includes(item.id));
	}, [itemsWithoutAudio, selectedIds]);

	const openDelete = (item: VocabCardAdminItem) => {
		setDeleteId(item.id);
		setDeleteTerm(item.term);
		setDeleteError(null);
	};

	const closeDelete = () => {
		setDeleteId(null);
		setDeleteTerm('');
		setDeleteError(null);
		setDeleteLoading(false);
	};

	const handleDelete = async () => {
		if (!deleteId) return;

		setDeleteLoading(true);
		setDeleteError(null);

		try {
			await deleteAdminVocabCard(deleteId);
			closeDelete();
			fetchData();
		} catch (e: any) {
			setDeleteError(getErrorMessage(e, 'Nie udało się usunąć słówka.'));
			setDeleteLoading(false);
		}
	};

	const toggleSelect = (id: number) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	const toggleSelectAllVisible = () => {
		if (allVisibleSelected) {
			setSelectedIds((prev) => prev.filter((id) => !items.some((item) => item.id === id)));
			return;
		}

		setSelectedIds((prev) => {
			const next = new Set(prev);
			items.forEach((item) => next.add(item.id));
			return Array.from(next);
		});
	};

	const selectAllMissingAudioOnPage = () => {
		setBulkAudioError(null);
		setBulkAudioMessage(null);

		setSelectedIds((prev) => {
			const next = new Set(prev);
			itemsWithoutAudio.forEach((item) => next.add(item.id));
			return Array.from(next);
		});
	};

	const selectFirstXMissingAudioOnPage = () => {
		setBulkAudioError(null);
		setBulkAudioMessage(null);

		const safeCount = Math.max(1, Math.min(100, Number(autoSelectCount) || 1));
		const ids = itemsWithoutAudio.slice(0, safeCount).map((item) => item.id);
		setSelectedIds(ids);
	};

	const clearSelection = () => {
		setSelectedIds([]);
		setBulkAudioError(null);
		setBulkAudioMessage(null);
	};

	const handleBulkGenerateAudio = async () => {
		if (!selectedIds.length) return;

		setBulkAudioLoading(true);
		setBulkAudioError(null);
		setBulkAudioMessage(null);

		try {
			const result = await generateAdminVocabCardsAudioBulk({
				card_ids: selectedIds,
			});

			setBulkAudioMessage(
				`Gotowe. Wygenerowano audio dla ${result.success_count} z ${result.total_selected} wybranych kart.`
			);

			setSelectedIds([]);
			await fetchData();
		} catch (e: any) {
			setBulkAudioError(getErrorMessage(e, 'Nie udało się wygenerować audio hurtowo.'));
		} finally {
			setBulkAudioLoading(false);
		}
	};

	const openImport = () => {
		setImportOpen(true);
		setImportError(null);
		setImportResult(null);
	};

	const closeImport = () => {
		setImportOpen(false);
		setImportError(null);
		setImportResult(null);

		const nextParams = new URLSearchParams(searchParams);
		nextParams.delete('import');
		setSearchParams(nextParams, { replace: true });
	};

	const handleImportFileLoad = (file: File) => {
		const reader = new FileReader();
		reader.onload = () => {
			setImportText(String(reader.result || ''));
		};
		reader.onerror = () => {
			setImportError('Nie udało się odczytać pliku JSON.');
		};
		reader.readAsText(file, 'utf-8');
	};

	const handleImportSubmit = async () => {
		setImportLoading(true);
		setImportError(null);
		setImportResult(null);

		try {
			let parsed: any;
			try {
				parsed = JSON.parse(importText);
			} catch {
				throw new Error('Niepoprawny JSON.');
			}

			const payload = Array.isArray(parsed)
				? {
						dry_run: importDryRun,
						update_existing: importUpdateExisting,
						items: parsed,
				  }
				: {
						...parsed,
						dry_run: importDryRun,
						update_existing: importUpdateExisting,
				  };

			const result = await importAdminVocabCardsJson(payload);
			setImportResult(result);

			if (!result.dry_run) {
				fetchData();
			}
		} catch (e: any) {
			setImportError(getErrorMessage(e, 'Import nie powiódł się.'));
		} finally {
			setImportLoading(false);
		}
	};

	return (
		<div className="contents-list-container">
			<div className="contents-header-bar">
				<h2>Słówka – karty</h2>

				<div className="action-buttons">
					<button className="btn ghost" onClick={() => setFiltersVisible((v) => !v)}>
						<span className="label">Filtry</span>
					</button>

					<button className="btn primary" onClick={() => navigate('/admin/vocab-cards/new')}>
						<span className="label">Dodaj słówko</span>
					</button>

					<button className="btn ghost" onClick={openImport}>
						<span className="label">Import JSON</span>
					</button>
				</div>
			</div>

			{filtersVisible && (
				<div className="filters-container">
					<div className="filters-row">
						<input
							type="text"
							placeholder="Szukaj po term / translation / definicji…"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>

						<select
	value={sourceLang}
	onChange={(e) => setSourceLang(e.target.value as VocabCardLang | '')}
>
							<option value="">Język źródłowy</option>
							{SOURCE_LANGS.filter(Boolean).map((lang) => (
								<option key={lang} value={lang}>
									{lang.toUpperCase()}
								</option>
							))}
						</select>

					<select
	value={cefrLevel}
	onChange={(e) => setCefrLevel(e.target.value as VocabCardCefr | '')}
>
							<option value="">Poziom CEFR</option>
							{CEFR_LEVELS.filter(Boolean).map((level) => (
								<option key={level} value={level}>
									{level}
								</option>
							))}
						</select>

						<select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
							<option value="">Kategoria</option>
							{categories.map((c) => (
								<option key={c.id} value={String(c.id)}>
									{c.title}
								</option>
							))}
						</select>

						<select value={isActive} onChange={(e) => setIsActive(e.target.value)}>
							<option value="">Status</option>
							<option value="true">Aktywne</option>
							<option value="false">Nieaktywne</option>
						</select>

						<select value={String(pageSize)} onChange={(e) => setPageSize(Number(e.target.value))}>
							<option value="10">10 / str.</option>
							<option value="20">20 / str.</option>
							<option value="50">50 / str.</option>
							<option value="100">100 / str.</option>
						</select>
					</div>
				</div>
			)}

			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 12,
					marginBottom: 16,
					padding: 12,
					border: '1px solid rgba(255,255,255,0.08)',
					borderRadius: 12,
					background: 'rgba(255,255,255,0.02)',
				}}
			>
				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: 10,
						alignItems: 'center',
					}}
				>
					<strong>Audio batch:</strong>

					<input
						type="number"
						min={1}
						max={100}
						value={autoSelectCount}
						onChange={(e) => setAutoSelectCount(Number(e.target.value) || 1)}
						style={{
							width: 90,
							padding: '8px 10px',
							borderRadius: 10,
							border: '1px solid rgba(255,255,255,0.12)',
							background: '#111',
							color: '#fff',
						}}
					/>

					<button
						className="btn ghost"
						onClick={selectFirstXMissingAudioOnPage}
						disabled={loading || itemsWithoutAudio.length === 0}
						title="Zaznacz pierwsze X słówek bez audio z aktualnej strony"
					>
						<span className="label">Zaznacz pierwsze X bez audio</span>
					</button>

					<button
						className="btn ghost"
						onClick={selectAllMissingAudioOnPage}
						disabled={loading || itemsWithoutAudio.length === 0}
					>
						<span className="label">Zaznacz wszystkie bez audio na stronie</span>
					</button>

					<button
						className="btn ghost"
						onClick={clearSelection}
						disabled={selectedIds.length === 0 && !bulkAudioMessage && !bulkAudioError}
					>
						<span className="label">Wyczyść zaznaczenie</span>
					</button>

					<button
						className="btn primary"
						onClick={handleBulkGenerateAudio}
						disabled={bulkAudioLoading || selectedIds.length === 0}
					>
						<span className="label">
							{bulkAudioLoading
								? 'Generowanie audio…'
								: `Generuj audio dla zaznaczonych (${selectedIds.length})`}
						</span>
					</button>
				</div>

				<div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 14, opacity: 0.9 }}>
					<span>
						Na stronie bez audio: <strong>{itemsWithoutAudio.length}</strong>
					</span>
					<span>
						Zaznaczone: <strong>{selectedIds.length}</strong>
					</span>
					<span>
						Zaznaczone bez audio: <strong>{selectedWithoutAudioCount}</strong>
					</span>
				</div>

				{bulkAudioError ? <div className="modal-error">{bulkAudioError}</div> : null}
				{bulkAudioMessage ? (
					<div
						style={{
							padding: '10px 12px',
							borderRadius: 10,
							background: 'rgba(60,180,120,0.12)',
							border: '1px solid rgba(60,180,120,0.25)',
						}}
					>
						{bulkAudioMessage}
					</div>
				) : null}
			</div>

			<div className="table-wrap">
				<table className="contents-table">
					<thead>
						<tr>
							<th style={{ width: 44 }}>
								<input
									type="checkbox"
									checked={allVisibleSelected}
									onChange={toggleSelectAllVisible}
									aria-label="Zaznacz wszystkie widoczne"
									title="Zaznacz wszystkie widoczne"
								/>
							</th>
							<th>ID</th>
							<th>Term</th>
							<th>Tłumaczenie</th>
							<th>Język</th>
							<th>CEFR</th>
							<th>Kategorie</th>
							<th>Polubienia</th>
							<th>Status</th>
							<th>Audio</th>
							<th>Data</th>
							<th></th>
						</tr>
					</thead>

					<tbody>
						{loading ? (
							<tr>
								<td colSpan={12}>Ładowanie…</td>
							</tr>
						) : items.length === 0 ? (
							<tr>
								<td colSpan={12}>Brak wyników.</td>
							</tr>
						) : (
							items.map((item) => {
								const categoryLabel =
									item.categories?.length > 0
										? item.categories.map((c) => c.title).join(', ')
										: '—';

								const isSelected = selectedIds.includes(item.id);
								const hasAudio = !!item.audio_path;

								return (
									<tr key={item.id}>
										<td>
											<input
												type="checkbox"
												checked={isSelected}
												onChange={() => toggleSelect(item.id)}
												aria-label={`Zaznacz słówko ${item.term}`}
											/>
										</td>
										<td>{item.id}</td>
										<td>
											<div className="cell-scroll" title={item.term}>
												{item.term}
											</div>
										</td>
										<td>
											<div className="cell-scroll" title={item.translation}>
												{item.translation}
											</div>
										</td>
										<td>
											{item.source_lang.toUpperCase()} → {item.target_lang.toUpperCase()}
										</td>
										<td>{item.cefr_level || '—'}</td>
										<td>
											<div className="cell-scroll" title={categoryLabel}>
												{categoryLabel}
											</div>
										</td>
										<td>{item.like_count ?? 0}</td>
										<td>
											<span className={`badge ${item.is_active ? 'badge-success' : 'badge-muted'}`}>
												{item.is_active ? 'AKTYWNE' : 'WYŁĄCZONE'}
											</span>
										</td>
										<td>
											{hasAudio ? (
												<span className="badge badge-success">TAK</span>
											) : (
												<span
													className={`badge ${isSelected && allMissingAudioSelected ? 'badge-warning' : 'badge-muted'}`}
												>
													BRAK
												</span>
											)}
										</td>
										<td>{new Date(item.created_at).toLocaleDateString()}</td>
										<td className="actions-cell">
											<button
												className="icon-btn edit"
												onClick={() => navigate(`/admin/vocab-cards/edit/${item.id}`)}
												aria-label="Edytuj"
												title="Edytuj"
											>
												<svg
													viewBox="0 0 24 24"
													width="16"
													height="16"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
												>
													<path d="M12 20h9"></path>
													<path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
												</svg>
											</button>

											<button
												className="icon-btn delete"
												onClick={() => openDelete(item)}
												aria-label="Usuń"
												title="Usuń"
											>
												<svg
													viewBox="0 0 24 24"
													width="16"
													height="16"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
												>
													<path d="M3 6h18"></path>
													<path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
													<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
												</svg>
											</button>
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginTop: 16,
					gap: 12,
					flexWrap: 'wrap',
				}}
			>
				<div>
					Razem: <strong>{total}</strong>
				</div>

				<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
					<button className="btn ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
						Poprzednia
					</button>
					<span>
						Strona <strong>{page}</strong> / <strong>{totalPages}</strong>
					</span>
					<button
						className="btn ghost"
						disabled={page >= totalPages}
						onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
					>
						Następna
					</button>
				</div>
			</div>

			<ConfirmModal
				open={deleteId !== null}
				title="Czy na pewno usunąć słówko?"
				description={`„${deleteTerm}” zostanie trwale usunięte.`}
				loading={deleteLoading}
				error={deleteError}
				onCancel={closeDelete}
				onConfirm={handleDelete}
			/>

			<ImportJsonModal
				open={importOpen}
				loading={importLoading}
				value={importText}
				setValue={setImportText}
				dryRun={importDryRun}
				setDryRun={setImportDryRun}
				updateExisting={importUpdateExisting}
				setUpdateExisting={setImportUpdateExisting}
				result={importResult}
				error={importError}
				onClose={closeImport}
				onSubmit={handleImportSubmit}
				onFileLoad={handleImportFileLoad}
			/>
		</div>
	);
}